/**
 * CyberAgent Panel — security posture, vulnerability scans, patches, threat model
 * Phase 4.8-B — WQ-200
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDashboard,
  listPatches,
  listIntelSources,
  getThreatModel,
  type CyberDashboard,
  type AutoPatch,
  type IntelSource,
  type ThreatModel,
} from '../../services/cyberAgentService';
import { fmt, safeRender } from '../../utils/formatNumber';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#3b82f6',
};

/**
 * WQ-224: STRIDE classification dict → compact label.
 * Backend returns {spoofing: N, tampering: N, repudiation: N, information_disclosure: N,
 * denial_of_service: N, elevation_of_privilege: N}. Not all keys always present.
 * Renders as "S:1 T:0 R:2" for nonzero entries only, or "0" if empty, "—" if null.
 */
function formatStride(strideDict: Record<string, number> | undefined | null): string {
  if (!strideDict || typeof strideDict !== 'object') return '—';
  const letters: Record<string, string> = {
    spoofing: 'S', tampering: 'T', repudiation: 'R',
    information_disclosure: 'I', denial_of_service: 'D', elevation_of_privilege: 'E',
  };
  const parts = Object.entries(strideDict)
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .map(([k, v]) => `${letters[k] ?? k[0]?.toUpperCase() ?? '?'}:${v}`);
  return parts.length > 0 ? parts.join(' ') : '0';
}

function PostureGauge({ score }: { score: number }) {
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 80;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size / 2 + 30} viewBox={`0 0 ${size} ${size / 2 + 30}`}>
      <path
        d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
        fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={14} strokeLinecap="round"
      />
      <path
        d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
        fill="none" stroke={color} strokeWidth={14} strokeLinecap="round"
        strokeDasharray={`${progress} ${circumference}`}
      />
      <text x={cx} y={cy - 10} textAnchor="middle" fill={color} fontSize={32} fontWeight={700}>
        {fmt(score, 0)}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize={12}>
        Security Posture
      </text>
    </svg>
  );
}

type ViewTab = 'overview' | 'patches' | 'intel' | 'threats';

const CyberAgentPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState<CyberDashboard | null>(null);
  const [patches, setPatches] = useState<AutoPatch[]>([]);
  const [intelSources, setIntelSources] = useState<IntelSource[]>([]);
  const [threatModels, setThreatModels] = useState<ThreatModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<ViewTab>('overview');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, patchData, intel, threats] = await Promise.all([
        getDashboard(authFetch),
        listPatches(authFetch, { limit: 20 }),
        listIntelSources(authFetch),
        getThreatModel(authFetch),
      ]);
      setDashboard(dash);
      setPatches(Array.isArray(patchData) ? patchData : []);
      setIntelSources(Array.isArray(intel) ? intel : []);
      setThreatModels(Array.isArray(threats) ? threats : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CyberAgent data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 12 }}>{error}</p>
        <button onClick={fetchData} className="glass" style={{ padding: '6px 16px', cursor: 'pointer', color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 6 }}>
          Retry
        </button>
      </div>
    );
  }

  if (!dashboard) return null;

  const { posture } = dashboard;
  if (!posture) {
    return (
      <div className="glass" style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
          No security posture assessment yet. Posture is computed after the first scan.
        </p>
      </div>
    );
  }

  const tabs: { key: ViewTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'patches', label: `Patches (${patches.length})` },
    { key: 'intel', label: `Intel (${intelSources.length})` },
    { key: 'threats', label: `Threats (${threatModels.length})` },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Posture Score + Vulnerability Counts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="glass" style={{ padding: 20, display: 'flex', justifyContent: 'center' }}>
          <PostureGauge score={posture.posture_score} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>{posture.open_critical}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Critical</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#f97316' }}>{posture.open_high}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>High</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#00d4ff' }}>{safeRender(dashboard.open_vulnerabilities, '0')}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Open Vulns</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>{dashboard.intel_sources}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Intel Sources</div>
          </div>
        </div>
      </div>

      {/* Posture Dimension Bars */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Posture Dimensions
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'Vulnerability', value: posture.vulnerability_score },
            { label: 'License', value: posture.license_score },
            { label: 'Patch Currency', value: posture.patch_currency_score },
            { label: 'OWASP', value: posture.owasp_score },
            { label: 'Threat Model', value: posture.threat_model_score },
          ].map(dim => (
            <div key={dim.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{dim.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: dim.value >= 80 ? '#22c55e' : dim.value >= 60 ? '#f59e0b' : '#ef4444' }}>
                  {fmt(dim.value, 0)}
                </span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                <div style={{
                  height: '100%', borderRadius: 3, transition: 'width 0.5s ease',
                  width: `${dim.value}%`,
                  background: dim.value >= 80 ? '#22c55e' : dim.value >= 60 ? '#f59e0b' : '#ef4444',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 4 }}>
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => setViewTab(t.key)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              background: viewTab === t.key ? 'rgba(0,212,255,0.15)' : 'transparent',
              color: viewTab === t.key ? '#00d4ff' : 'rgba(255,255,255,0.5)',
              transition: 'all 0.2s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Overview: Recent Scans */}
      {viewTab === 'overview' && (
        <div className="glass" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
            Recent Scans
          </h3>
          {dashboard.recent_scans.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No scans recorded yet</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Target</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Vulns</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: SEVERITY_COLORS.critical, fontWeight: 500 }}>C</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: SEVERITY_COLORS.high, fontWeight: 500 }}>H</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: SEVERITY_COLORS.medium, fontWeight: 500 }}>M</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: SEVERITY_COLORS.low, fontWeight: 500 }}>L</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Patched</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent_scans.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '8px 12px', textTransform: 'capitalize' }}>{s.scan_type}</td>
                      <td style={{ padding: '8px 12px', color: 'rgba(255,255,255,0.6)' }}>{s.scan_target}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{s.vulnerabilities_found}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: s.critical_count > 0 ? SEVERITY_COLORS.critical : 'rgba(255,255,255,0.3)' }}>{s.critical_count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: s.high_count > 0 ? SEVERITY_COLORS.high : 'rgba(255,255,255,0.3)' }}>{s.high_count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: s.medium_count > 0 ? SEVERITY_COLORS.medium : 'rgba(255,255,255,0.3)' }}>{s.medium_count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: s.low_count > 0 ? SEVERITY_COLORS.low : 'rgba(255,255,255,0.3)' }}>{s.low_count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#22c55e' }}>{s.auto_patches_applied}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                        {new Date(s.scanned_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Patches View */}
      {viewTab === 'patches' && (
        <div className="glass" style={{ padding: 20 }}>
          {patches.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No patches applied</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {patches.map(p => (
                <div key={p.id} className="glass" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: p.status === 'applied' ? 'rgba(34,197,94,0.15)' : 'rgba(249,115,22,0.15)',
                    color: p.status === 'applied' ? '#22c55e' : '#f97316',
                  }}>{p.status}</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{p.package_name}</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>
                    {p.old_version} &rarr; {p.new_version}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{p.package_manager}</span>
                  {p.cve_ids.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {p.cve_ids.map(cve => (
                        <span key={cve} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
                          {cve}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Intel Sources View */}
      {viewTab === 'intel' && (
        <div className="glass" style={{ padding: 20 }}>
          {intelSources.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No intel sources registered</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {intelSources.map(src => (
                <div key={src.id} className="glass" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{src.name}</span>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: src.is_active ? '#22c55e' : '#6b7280',
                    }} />
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(0,212,255,0.1)', color: '#00d4ff' }}>
                      {src.source_category}
                    </span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>
                      {src.source_type}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Items: {src.last_item_count}</span>
                    <span>{src.last_checked_at ? new Date(src.last_checked_at).toLocaleDateString() : 'Never checked'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Threat Model View */}
      {viewTab === 'threats' && (
        <div className="glass" style={{ padding: 20 }}>
          {threatModels.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No threat models assessed</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {threatModels.map(tm => (
                <div key={tm.id} className="glass" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{tm.surface_name}</span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(124,58,237,0.1)', color: '#7c3aed' }}>
                      {tm.surface_type}
                    </span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}>
                      STRIDE: {safeRender(formatStride(tm.stride_classification), '—')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                    <span>Endpoints: {tm.endpoints_count}</span>
                    <span>Threats: {Object.keys(tm.threats ?? {}).length}</span>
                    <span>Mitigations: {Object.keys(tm.mitigations ?? {}).length}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                      Assessed: {new Date(tm.last_assessed_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CyberAgentPanel;
